/**
 * Whether the navigation rail is pinned open.
 *
 * ## The model, which changed with the design system
 *
 * This used to be two remembered booleans — the trader's choice on the Chart
 * screen and their choice everywhere else — because ten unlabelled glyphs is a
 * memory test, but ~130px of nav beside a chart is 130px of price action, and
 * one boolean could not express both.
 *
 * The design system answers that with a better idea, so the two booleans are
 * gone. **Collapsed is the resting state everywhere, and hovering the rail
 * peeks the labels out OVER the content without moving the layout.** That gives
 * the reading the labels existed for without ever charging the chart for it, so
 * there is no longer a per-screen tradeoff to remember. What is left is one
 * decision: does this trader want the column permanently wider.
 *
 * The peek itself is deliberately NOT stored. It is a hover state, it lasts as
 * long as the pointer does, and persisting it would mean a rail that opened on
 * load because of where the mouse happened to be an hour ago.
 *
 * ## Why localStorage and not the preferences API
 *
 * This is a property of the window, not of the trader. The same account on a
 * 13" laptop and a 32" monitor wants different answers, and a server-stored
 * preference would sync the laptop's answer onto the monitor.
 */

export const DEFAULT_PINNED = false;

const KEY = 'aegis.rail.pinned';

const storage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari in private mode throws on access rather than returning null.
    return null;
  }
};

export function loadPinned(): boolean {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(KEY) ?? null;
  } catch {
    // `storage()` guards access to the object; reading a key can throw
    // separately, and a rail that crashes the app on boot is worse than a rail
    // that forgets which way it was pointing.
    return DEFAULT_PINNED;
  }
  // Anything other than the two values written here is treated as unset rather
  // than as truthy, so a stale value from the old two-boolean shape (which
  // stored JSON) degrades to the default instead of pinning the rail open.
  return raw === '1' ? true : raw === '0' ? false : DEFAULT_PINNED;
}

export function savePinned(pinned: boolean): void {
  try {
    storage()?.setItem(KEY, pinned ? '1' : '0');
  } catch {
    // Quota or private mode. The rail still works for this session; losing the
    // preference is not worth throwing inside a click handler.
  }
}

/**
 * How long a pointer must rest on the rail before the labels slide out.
 *
 * A hover is not always an intention — the pointer crosses the rail on its way
 * to the chart's left edge. Long enough to filter a pass-through, short enough
 * that a deliberate hover feels immediate. Closing is instant: nobody wants to
 * wait to get their chart back.
 */
export const PEEK_DELAY_MS = 140;

/** `⌘` on Apple platforms, `^` elsewhere. The rail advertises the shortcut. */
export function modifierKey(platform: string): string {
  return /mac|iphone|ipad/i.test(platform) ? '⌘' : '^';
}

/**
 * The section index a `⌘1`…`⌘0` chord selects, or null.
 *
 * `⌘0` is the tenth, matching how browsers number tabs. Returns null for
 * anything that is not a bare modifier+digit so the handler never swallows a
 * chord the app does not own.
 */
export function shortcutIndex(
  key: string,
  mods: { meta: boolean; ctrl: boolean; alt: boolean; shift: boolean },
): number | null {
  if (!(mods.meta || mods.ctrl) || mods.alt || mods.shift) return null;
  if (!/^[0-9]$/.test(key)) return null;
  return key === '0' ? 9 : Number(key) - 1;
}
